import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import type { IBlogArticlesNode as IBlogAllQueryNode } from "@repo/shopify/types/api/blog";
import type { ArticleEdge } from "@repo/shopify/types/generated/storefront.types";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useBlog from "@queries/useBlog";

import { useLayoutStore } from "@store/layout";

export default function useFeaturesDetail(handle: string, query: string) {
  const navigate = useNavigate();
  const location = useLocation();

  const { handle: articleHandle } = useParams();

  const { setMeta } = useLayoutStore();

  const { fetchBlog, blogData, blogLoading } = useBlog();

  // 목록에서 글을 넘겨받아 들어온 경우(location.state)에는 조회하지 않는다
  useEffect(() => {
    if (location.state?.article) return;

    void fetchBlog({ handle, articleHandle, cursor_first: 200, cursor_sortKey: "PUBLISHED_AT", cursor_query: query });
  }, [handle, articleHandle, query, location.state]);

  const article = (location.state?.article as IBlogAllQueryNode | undefined) ?? blogData?.blog.articleByHandle;

  const cursorList = useMemo(
    () =>
      ((location.state?.cursorList as (ArticleEdge & { node: IBlogAllQueryNode })[] | undefined) ??
        blogData?.blog.cursor_list?.edges ??
        []) as (ArticleEdge & { node: IBlogAllQueryNode })[],
    [location.state, blogData],
  );

  useEffect(() => {
    if (blogData && !blogData.blog.articleByHandle) navigate(URLS.HOME);
  }, [blogData]);

  const { prevArticle, nextArticle } = useMemo(() => {
    const index = cursorList.findIndex(({ node }) => node?.id === article?.id);
    if (index < 0) return { prevArticle: null, nextArticle: null };

    return {
      nextArticle: index > 0 ? (cursorList[index - 1]?.node ?? null) : null,
      prevArticle: index < cursorList.length ? (cursorList[index + 1]?.node ?? null) : null,
    };
  }, [cursorList, article]);

  useEffect(() => {
    if (article?.title) {
      setMeta({ title: `${article?.title.replaceAll("<br>", "")} - ${DEFAULT_TITLE}` });
    }
  }, [article?.title]);

  return { loading: blogLoading, article, prevArticle, nextArticle };
}
